import { Job } from "@domain/CRM/job";
import type { IJobs } from "@ports/jobs/IJobs";
import type { IJobIssues } from "@ports/jobs/IJobIssues";

/**
 * The mode encodes the operator's override for which sync channel to use
 * when re-trying the sendback:
 *  - `"normal"` → respect the deal's configured sync method (no forcing).
 *  - `"force-email"` → re-send the lab-CSV email regardless of deal config.
 *  - `"force-api"` → re-send through the lab-API integration regardless.
 */
export type ResendMode = "normal" | "force-email" | "force-api";

interface DataSendbackIssuesUseCasesDeps {
	jobIssuesApi: IJobIssues;
	jobsApi: IJobs;
}

/**
 * Drives the Admin → Data Sendback Issues page. Two distinct fix paths:
 *
 *  - `resend(ids, mode)` — kick off the backend sendback retry. Mode maps
 *    to the fixDataSendback `forceEmail`/`forceApi` flags. Backend is
 *    asynchronous; the vm runs its visible countdown after this returns.
 *
 *  - `markCsvSent(jobs)` — mark a batch of jobs as `csvSentToLab: true` via
 *    `IJobs.updateMany`. This is a *direct* update to the jobs table, not
 *    a kick-off; once it returns the rows are persisted and the vm can
 *    refetch immediately (no countdown needed). Constructs fresh `Job`
 *    instances with the flag flipped so the view-tier doesn't need
 *    `Object.assign(new Job(), ...)` boilerplate.
 *
 * The vm keeps the countdown timer, fixingIds set, search/sort/filter state,
 * and selection state.
 */
export interface DataSendbackIssuesUseCases {
	loadIssues(): Promise<Job[]>;
	resend(ids: string[], mode: ResendMode): Promise<void>;
	markCsvSent(jobs: Job[]): Promise<void>;
}

function loadIssues(deps: DataSendbackIssuesUseCasesDeps): Promise<Job[]> {
	return deps.jobIssuesApi.getDataSendback();
}

async function resend(deps: DataSendbackIssuesUseCasesDeps, ids: string[], mode: ResendMode): Promise<void> {
	await deps.jobIssuesApi.fixDataSendback(ids, mode === "force-email", mode === "force-api");
}

async function markCsvSent(deps: DataSendbackIssuesUseCasesDeps, jobs: Job[]): Promise<void> {
	const updates = jobs.map((j) => Object.assign(new Job(), j, { csvSentToLab: true }));
	await deps.jobsApi.updateMany(updates);
}

export function makeDataSendbackIssuesUseCases(deps: DataSendbackIssuesUseCasesDeps): DataSendbackIssuesUseCases {
	return {
		loadIssues: () => loadIssues(deps),
		resend: (ids, mode) => resend(deps, ids, mode),
		markCsvSent: (jobs) => markCsvSent(deps, jobs),
	};
}
